// Radha — Collections data, straight from PostgREST.
//
// The template. Where useCollections.ts calls /api/v1/collections through the
// gateway, this queries Supabase directly and lets RLS scope the tenant. Same
// TanStack Query surface, same types, so a page swaps one import and nothing
// else changes. The other eight modules copy this file, change the table names,
// and they are done — the tenant isolation is already in the database.
//
// There is deliberately NO tenant id anywhere in these queries. Sending one
// would be both pointless and unsafe: RLS derives the tenant from the auth
// token via auth_tenant_id(), so a client that asked for another tenant's rows
// would simply receive nothing.
import { useQuery } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import type {
  CollectionAccount,
  CollectionProposal,
  RecoveryForecast,
} from "../types/collections";

export const collectionsKeys = {
  accounts: ["collections-direct", "accounts"] as const,
  proposals: ["collections-direct", "proposals"] as const,
  forecasts: (accountId: string) =>
    ["collections-direct", "forecasts", accountId] as const,
};

export function useAccounts() {
  return useQuery({
    queryKey: collectionsKeys.accounts,
    queryFn: async (): Promise<CollectionAccount[]> => {
      const { data, error } = await supabase
        .from("collection_accounts")
        .select(
          "id, debtor_name, debtor_gstin, debtor_pan_masked, debtor_state, " +
            "outstanding_amount, original_amount, days_past_due, stage, " +
            "risk_score, risk_band, recovery_probability, is_msme, " +
            "payment_terms_days, samadhaan_eligible, consent_to_contact, " +
            "assigned_to, created_at",
        )
        .order("days_past_due", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as CollectionAccount[];
    },
    staleTime: 30_000,
  });
}

export function useForecasts(accountId: string) {
  return useQuery({
    queryKey: collectionsKeys.forecasts(accountId),
    enabled: !!accountId,
    queryFn: async (): Promise<RecoveryForecast[]> => {
      const { data, error } = await supabase
        .from("recovery_forecasts")
        .select(
          "id, account_id, forecast_date, expected_recovery_amount, " +
            "expected_recovery_days, confidence, model_version",
        )
        .eq("account_id", accountId)
        .order("forecast_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as RecoveryForecast[];
    },
  });
}

export function useProposals() {
  return useQuery({
    queryKey: collectionsKeys.proposals,
    queryFn: async (): Promise<CollectionProposal[]> => {
      const { data, error } = await supabase
        .from("collection_proposals")
        .select(
          "id, account_id, tier, signal_summary, recommended_action, " +
            "recommended_channel, recommended_language, draft_message, " +
            "confidence, status, created_at",
        )
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as CollectionProposal[];
    },
    staleTime: 30_000,
  });
}
